import React from "react";

/**
 * PageHero — modern hero header used at the top of each migration page.
 *
 * Visually consistent across PC / Server / Storage migration pages and
 * any other surface that wants a "this is a SaaS dashboard, not a
 * config form" first impression. Renders an icon badge, eyebrow label,
 * page title, optional description, and a right-aligned slot for the
 * page-level actions (refresh / new / etc).
 *
 * Pure visual wrapper — no business logic, no data fetches. Callers
 * keep ownership of every button and handler so the existing test IDs
 * and click paths continue to work unchanged.
 */
export default function PageHero({
    eyebrow,
    title,
    description,
    icon: Icon,
    tint = "#5b5ef0",
    actions,
    testId,
}) {
    return (
        <div
            data-testid={testId || "page-hero"}
            className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-surface to-background p-5 shadow-sm sm:p-6"
        >
            {/* Soft accent glow — pinned to the right so it doesn't compete
                with the heading. Hidden on small screens to save space. */}
            <div
                aria-hidden
                className="pointer-events-none absolute -right-24 -top-24 hidden h-64 w-64 rounded-full opacity-25 blur-3xl sm:block"
                style={{ background: `radial-gradient(closest-side, ${tint}80, transparent 70%)` }}
            />

            <div className="relative flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                    {Icon && (
                        <div
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border"
                            style={{
                                background: `${tint}1f`,
                                color: tint,
                                borderColor: `${tint}33`,
                            }}
                        >
                            <Icon size={20} />
                        </div>
                    )}
                    <div className="min-w-0">
                        {eyebrow && (
                            <div
                                className="font-mono text-[10px] font-semibold uppercase tracking-[0.28em]"
                                style={{ color: tint }}
                            >
                                {eyebrow}
                            </div>
                        )}
                        <h1
                            className="mt-1 font-display text-2xl font-bold tracking-tight sm:text-[28px]"
                            data-testid={testId ? `${testId}-title` : "page-hero-title"}
                        >
                            {title}
                        </h1>
                        {description && (
                            <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
                                {description}
                            </p>
                        )}
                    </div>
                </div>

                {actions && (
                    <div className="flex flex-wrap items-center gap-2">
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
}
